import {
  FormControl,
  FormErrorMessage,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
} from "@chakra-ui/react";
import { DEFAULT_LANGUAGE, Language, translate as t } from "@models/language";
import {
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import DynamicFormFieldHeader from "./DynamicFormFieldHeader";
import { Field } from "./types";

const DynamicFormInputNumber = ({
  field,
  errors,
  register,
  setValue,
  language: language = DEFAULT_LANGUAGE,
  isDirty: isDirty = true,
  className: className = "",
}: {
  field: Field;
  errors: FieldErrors<FieldValues>;
  register: UseFormRegister<FieldValues>;
  language?: Language;
  isDirty?: boolean;
  className?: string;
  setValue?: UseFormSetValue<FieldValues>;
}) => {
  // TODO: default translation
  const _t = (key?: string, language = DEFAULT_LANGUAGE) => key || "";

  // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
  let colSpan = "sm:col-span-1";
  switch (field.span) {
    case 1:
      colSpan = "sm:col-span-1";
      break;
    case 2:
      colSpan = "sm:col-span-2";
      break;
    case 3:
      colSpan = "sm:col-span-3";
      break;
    case 4:
      colSpan = "sm:col-span-4";
      break;
  }

  const toRegisterOptions = (
    field: Field
  ): RegisterOptions<FieldValues, string> => {
    return {
      ...(field.max
        ? {
            max: {
              value: field.max,
              message:
                t(field.errorMessages?.max, language) ||
                _t(`Maximum is ${field.max}`, language),
            },
          }
        : {}),
      ...(field.min
        ? {
            min: {
              value: field.min,
              message:
                t(field.errorMessages?.min, language) ||
                _t(`Minimum is ${field.min}`, language),
            },
          }
        : {}),
      ...(field.required
        ? {
            required:
              t(field.errorMessages?.required, language) ||
              _t("This field is required", language),
          }
        : {}),
      ...(field.value
        ? {
            value: field.value,
          }
        : {}),
      ...(field.pattern
        ? {
            pattern: {
              value: new RegExp(field.pattern, "gm"),
              message:
                t(field.errorMessages?.pattern, language) ||
                _t("The input is not valid", language),
            },
          }
        : {}),
    };
  };

  const handleInputChange = (event) => {
    if (!setValue) return;
    setValue(field.key, event);
  };

  return (
    <>
      <FormControl
        isInvalid={isDirty && !!errors[field.key]}
        className={`${field.span ? colSpan : ""} col-span-1 ${className}`}
        isRequired={field.required}
        isDisabled={field.disabled}
        isReadOnly={field.readonly}
      >
        <DynamicFormFieldHeader field={field} language={language} />
        <NumberInput
          id={field.key}
          min={field.min}
          max={field.max}
          onChange={handleInputChange}
        >
          <NumberInputField
            {...register(field.key, {
              ...toRegisterOptions(field),
            })}
          />
          {!!setValue && (
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          )}
        </NumberInput>
        <FormErrorMessage>
          {String(errors[field.key]?.message || "")}
        </FormErrorMessage>
      </FormControl>
    </>
  );
};

export default DynamicFormInputNumber;
